import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import __ from 'utils/i18n';
import Loader from 'components/Loader';
import { PlansTable } from './components/PlansTable';
import { PlansReportWidget } from './components/PlansReportWidget';
import { SessionAccessLinkWidget } from './components/SessionAccessLinkWidget';
import { PlanPanel } from './components/PlanPanel';
import { MerchantConfigPanel } from './components/MerchantConfigPanel';
import DashboardPanel from 'containers/Dashboard/components/DashboardPanel';
import ServerSideSearch from 'components/ServerSideSearch';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col } from 'reactstrap';
import SelectInput from 'components/forms/inputs/SelectInput';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { fetchResourceRP } from '../_shared/fetchResourceRP';
import { getPlans } from './_shared/resources';
import BottomAlert from 'components/BottomAlert';
import useSuccessAlert from 'hooks/useSuccessAlert';
import { BackendRpErrorResponse } from '../_shared/BackendRpErrorResponse';
import TableHeaderActions from 'components/TableHeaderActions';
import { notification } from 'utils/notifications';
import gtmEvents from 'utils/gtmEvents';

export const PlansTab = ({ onNavigateToSubscribers, showReportsPanel, onCloseReportsPanel }) => {
  const { token } = useSelector((state) => state.api);
  const merchantId = useSelector((state) => state.login.user?.merchantId);

  useEffect(() => {
    gtmEvents.recurringPaymentsPageView('plans', merchantId);
  }, [merchantId]);
  
  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [queryParams, setQueryParams] = useState({});
  
  const [showLoader, setShowLoader] = useState(true);
  const isFirstRender = React.useRef(true);

  const [items, setItems] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [frequencyFilter, setFrequencyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Hook para manejar alertas de éxito
  const { alertProps } = useSuccessAlert();
  
  // useQuery con paginación y filtros enviados al backend
  const { data, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['recurringPayments', 'plans', currentPage, pageSize, queryParams],
    queryFn: () => {
      const params = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        ...queryParams,
      };
      return fetchResourceRP(getPlans(token), params);
    },
    enabled: !!token,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      setShowLoader(false);
      console.error('[PlansTab] Error loading plans:', error);
      notification.error(BackendRpErrorResponse.firstErrorMessage(error));
    },
  });

  React.useEffect(() => {
    const params = {};
    
    if (frequencyFilter) {
      params.frequency = frequencyFilter;
    }
    
    if (statusFilter) {
      params.status = statusFilter === 'Cancelled' ? 'inactive' : statusFilter.toLowerCase();
    }
    
    // Add search parameter - searchText is already sanitized by ServerSideSearch component
    // and only contains values that meet minLength requirement (or empty)
    if (searchText && searchText.trim().length > 0) {
      params.search = searchText.trim();
    }

    setQueryParams(params);
    
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      setCurrentPage(1);
      setShowLoader(true);
    }
  }, [frequencyFilter, statusFilter, searchText]);

  React.useEffect(() => {
    if (data?.data) {
      setItems(data.data);
      setShowLoader(false);
    }
  }, [data]);

  React.useEffect(() => {
    if (showReportsPanel) {
      setShowCreatePanel(false);
      setShowDetails(false);
      setSelectedPlan(null);
    }
  }, [showReportsPanel]);

  const reload = () => {
    refetch();
  };
  
  const clearFilters = () => {
    setFrequencyFilter('');
    setStatusFilter('');
    setSearchText('');
  };
  
  const hasActiveFilters = frequencyFilter || statusFilter || searchText;

  const handleCreatePlan = () => {
    setShowCreatePanel(true);
    onCloseReportsPanel?.();
    setShowDetails(false);
    setSelectedPlan(null);
  };

  const handlePlanCreated = (createdPlan) => {
    setShowCreatePanel(false);
    refetch();
    notification.success(__('dashboard.sections.recurringPayments.plans.planCreatedSuccess'));
    setSelectedPlan(createdPlan);
    setShowDetails(true);
  };

  const handleViewSubscribers = (plan) => {
    setShowDetails(false);
    setSelectedPlan(null);
    
    if (onNavigateToSubscribers) {
      onNavigateToSubscribers(plan);
    }
  };

  return (
    <div
      className={classnames({
        'dashboard-card': true,
        'panel-expanded': showDetails || showCreatePanel || showReportsPanel,
      })}
    >
      {/* Panel para crear nuevo plan */}
      <DashboardPanel
        panelHeading={__('dashboard.sections.recurringPayments.plans.form.title')}
        expanded={showCreatePanel}
        hidePanel={() => setShowCreatePanel(false)}
      >
        {showCreatePanel && (
          <PlanPanel 
            data={null} 
            onSave={handlePlanCreated}
            hidePanel={() => setShowCreatePanel(false)}
            showDetails={showCreatePanel}
          />
        )}
      </DashboardPanel>

      <DashboardPanel
        panelHeading={__('dashboard.sections.recurringPayments.plans.merchantConfig.title')}
        expanded={showReportsPanel}
        hidePanel={() => onCloseReportsPanel?.()}
      >
        {showReportsPanel && <MerchantConfigPanel />}
      </DashboardPanel>

      <div className="dashboard-card-header recurring-payments-card-header">
        {!isLoading && (
          <div className="d-flex align-items-top" style={{ gap: '10px' }}>
            <ServerSideSearch
              value={searchText}
              onChange={setSearchText}
              minLength={3}
              maxLength={100}
            />
            
            <div style={{ width: '180px' }}>
              <SelectInput
                options={[
                  { value: '', label: __('dashboard.sections.recurringPayments.plans.filters.frequency.label') },
                  { value: 'Weekly', label: __('dashboard.sections.recurringPayments.plans.filters.frequency.weekly') },
                  { value: 'Monthly', label: __('dashboard.sections.recurringPayments.plans.filters.frequency.monthly') },
                  { value: 'Quarterly', label: __('dashboard.sections.recurringPayments.plans.filters.frequency.quarterly') },
                  { value: 'Yearly', label: __('dashboard.sections.recurringPayments.plans.filters.frequency.yearly') }
                ]}
                value={frequencyFilter}
                onChange={(value) => setFrequencyFilter(value || '')}
                placeholder={__('dashboard.sections.recurringPayments.plans.filters.frequency.label')}
                isSearchable={false}
              />
            </div>
            
            <div style={{ width: '180px' }}>
              <SelectInput
                options={[
                  { value: '', label: __('dashboard.sections.recurringPayments.plans.filters.status.label') },
                  { value: 'Active', label: __('dashboard.sections.recurringPayments.plans.filters.status.active') },
                  { value: 'Cancelled', label: __('dashboard.sections.recurringPayments.plans.table.statuses.cancelled') }
                ]}
                value={statusFilter}
                onChange={(value) => setStatusFilter(value || '')}
                placeholder={__('dashboard.sections.recurringPayments.plans.filters.status.label')}
                isSearchable={false}
              />
            </div>
            
            {hasActiveFilters && (
              <Button
                color="link"
                onClick={clearFilters}
                style={{ whiteSpace: 'nowrap' }}
              >
                {__('dashboard.sections.recurringPayments.plans.filters.clearFilters')}
              </Button>
            )}
          </div>
        )}
        <div className="recurring-payments-header-actions">
          {data?.data && data.data.length > 0 && (
            <>
              <div className="mr-2">
                <SessionAccessLinkWidget />
              </div>
              <div className="mr-2">
                <PlansReportWidget currentFilters={queryParams} />
              </div>
            </>
          )}
          <TableHeaderActions
            createItem={{
              name: __('dashboard.sections.recurringPayments.plans.actions.createPlan'),
              action: handleCreatePlan,
            }}
          />
        </div>
      </div>
      
      <div className="dashboard-card-body">
        {(isLoading || showLoader) && <Loader />}
        {(!items.length && !isLoading && !showLoader) && (
          <div className="dashboard-card-message">
            <div className="d-flex justify-content-center mb-3">
              <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: '120px', height: '120px' }}>
                <i className="mdi mdi-file-document-outline" style={{ fontSize: '60px', color: '#999' }} />
              </div>
            </div>
            <h3>{__('dashboard.sections.recurringPayments.plans.noPlans')}</h3>
            <button
              type="button"
              onClick={() => setShowHelpModal(true)}
              className="btn btn-link p-0 text-primary"
            >
              {__('dashboard.sections.recurringPayments.plans.howItWorks')}
            </button>
          </div>
        )}
        {(items.length > 0 && !isLoading && !showLoader) && (
          <>
            <PlansTable
              plans={items}
              total={data?.pagination?.total_count || items.length}
              isFetching={isFetching}
              reload={reload}
              showDetails={showDetails}
              setShowDetails={setShowDetails}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              onViewSubscribers={handleViewSubscribers}
            />
            
            {/* Controles de paginación */}
            {data?.pagination && data.pagination.total_count > pageSize && (
              <div className="d-flex justify-content-between align-items-center mt-3 px-3 pb-3">
                <div className="text-muted">
                  {__('dashboard.sections.recurringPayments.subscribers.showing')} {' '}
                  {Math.min((currentPage - 1) * pageSize + 1, data.pagination.total_count)} - {' '}
                  {Math.min(currentPage * pageSize, data.pagination.total_count)} {' '}
                  {__('dashboard.sections.recurringPayments.subscribers.of')} {data.pagination.total_count}
                </div>
                <div className="d-flex align-items-center">
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => {
                      setShowLoader(true);
                      setCurrentPage(p => Math.max(1, p - 1));
                    }}
                    className="mr-2 d-flex align-items-center justify-content-center"
                    style={{ minWidth: '32px', minHeight: '32px' }}
                  >
                    <i className="mdi mdi-chevron-left" style={{ lineHeight: 1 }} />
                  </Button>
                  <span className="mx-2">
                    {__('dashboard.sections.recurringPayments.subscribers.page')} {currentPage} {' '}
                    {__('dashboard.sections.recurringPayments.subscribers.of')} {' '}
                    {Math.ceil(data.pagination.total_count / pageSize)}
                  </span>
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    disabled={currentPage >= Math.ceil(data.pagination.total_count / pageSize)}
                    onClick={() => {
                      setShowLoader(true);
                      setCurrentPage(p => p + 1);
                    }}
                    className="ml-2 d-flex align-items-center justify-content-center"
                    style={{ minWidth: '32px', minHeight: '32px' }}
                  >
                    <i className="mdi mdi-chevron-right" style={{ lineHeight: 1 }} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de ayuda */}
      <Modal isOpen={showHelpModal} toggle={() => setShowHelpModal(false)} size="lg">
        <ModalHeader toggle={() => setShowHelpModal(false)}>
          {__('dashboard.sections.recurringPayments.plans.howItWorks')}
        </ModalHeader>
        <ModalBody>
          <p className="text-center mb-4">
            {__('dashboard.sections.recurringPayments.plans.helpModal.description')}
          </p>
          
          <Row className="mb-4">
            <Col md="4" className="text-center mb-3">
              <div className="d-flex justify-content-center mb-3">
                <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px' }}>
                  <i className="mdi mdi-sync text-primary" style={{ fontSize: '40px' }}></i>
                </div>
              </div>
              <h6 className="font-weight-bold mb-2">
                {__('dashboard.sections.recurringPayments.plans.helpModal.howItWorks.title')}
              </h6>
              <ul className="text-left small" style={{ listStyle: 'disc', paddingLeft: '20px' }}>
                <li>{__('dashboard.sections.recurringPayments.plans.helpModal.howItWorks.step1')}</li>
                <li>{__('dashboard.sections.recurringPayments.plans.helpModal.howItWorks.step2')}</li>
                <li>{__('dashboard.sections.recurringPayments.plans.helpModal.howItWorks.step3')}</li>
              </ul>
            </Col>

            <Col md="4" className="text-center mb-3">
              <div className="d-flex justify-content-center mb-3">
                <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px' }}>
                  <i className="mdi mdi-account-multiple text-primary" style={{ fontSize: '40px' }}></i>
                </div>
              </div>
              <h6 className="font-weight-bold mb-2">
                {__('dashboard.sections.recurringPayments.plans.helpModal.whatCanManage.title')}
              </h6>
              <ul className="text-left small" style={{ listStyle: 'disc', paddingLeft: '20px' }}>
                <li>{__('dashboard.sections.recurringPayments.plans.helpModal.whatCanManage.plans')}</li>
                <li>{__('dashboard.sections.recurringPayments.plans.helpModal.whatCanManage.subscribers')}</li>
                <li>{__('dashboard.sections.recurringPayments.plans.helpModal.whatCanManage.charges')}</li>
              </ul>
            </Col>

            <Col md="4" className="text-center mb-3">
              <div className="d-flex justify-content-center mb-3">
                <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px' }}>
                  <i className="mdi mdi-account text-primary" style={{ fontSize: '40px' }}></i>
                </div>
              </div>
              <h6 className="font-weight-bold mb-2">
                {__('dashboard.sections.recurringPayments.plans.helpModal.subscriberPortal.title')}
              </h6>
              <p className="text-left small">
                {__('dashboard.sections.recurringPayments.plans.helpModal.subscriberPortal.description')}
              </p>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="link" onClick={() => setShowHelpModal(false)}>
            {__('dashboard.sections.recurringPayments.plans.helpModal.faq')}
          </Button>
          <Button color="primary" onClick={() => {
            setShowHelpModal(false);
            handleCreatePlan();
          }}>
            {__('dashboard.sections.recurringPayments.plans.actions.createPlan')}
          </Button>
        </ModalFooter>
      </Modal>
      
      <BottomAlert {...alertProps} />
    </div>
  );
};
